import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { motion } from 'framer-motion';
import { getSkinSrc } from '../components/SeasonalSkins';
import LoadingSpinner from './LoadingSpinner';
import '../index.css';

const API_URL = process.env.REACT_APP_API_URL;

const wait = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const normalizeNumber = (value, fallback = 0) => {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const normalizeBoolean = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true'
  );
};

const normalizePrizeType = (value) => {
  const type = String(value ?? '')
    .trim()
    .toLowerCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_');

  if (
    [
      'cardpieces',
      'cardpiece',
      'card_piece',
      'card_pieces',
      'card_fragments',
      'card_fragment',
    ].includes(type)
  ) {
    return 'card_pieces';
  }

  if (
    [
      'spin',
      'spins',
      'fortune_spin',
      'fortune_spins',
      'fortune_wheel',
    ].includes(type)
  ) {
    return 'spins';
  }

  if (
    [
      'seasonalaccessory',
      'seasonal_accessory',
      'skin',
      'skins',
    ].includes(type)
  ) {
    return 'seasonal_accessory';
  }

  if (
    [
      'rune',
      'runes',
    ].includes(type)
  ) {
    return 'runes';
  }

  if (
    [
      'spark',
      'sparks',
    ].includes(type)
  ) {
    return 'sparks';
  }

  if (
    [
      'booster',
      'boosters',
      'energy',
    ].includes(type)
  ) {
    return 'booster';
  }

  if (
    [
      'ability',
      'abilities',
    ].includes(type)
  ) {
    return 'ability';
  }

  if (
    [
      'card',
      'cards',
      'character',
      'player_card',
    ].includes(type)
  ) {
    return 'card';
  }

  if (
    [
      'shield',
      'trophy_shield',
    ].includes(type)
  ) {
    return 'shield';
  }

  if (
    [
      'evolve',
      'evolution',
      'evolvement',
    ].includes(type)
  ) {
    return 'evolve';
  }

  if (
    [
      'gamepass',
      'game_pass',
      'battle_pass',
    ].includes(type)
  ) {
    return 'gamepass';
  }

  return type;
};

const getSeasonalFragmentCount = (item) => {
  return normalizeNumber(
    item?.accessory_total_items ??
      item?.accessoryTotalItems ??
      item?.total_fragments ??
      item?.totalFragments ??
      item?.fragments_required ??
      item?.fragmentsRequired ??
      item?.required_fragments ??
      item?.requiredFragments,
    0
  );
};

const getPrizeCardRarity = (item) => {
  const possibleValues = [
    item?.card_rarity,
    item?.cardRarity,
    item?.rarity,
    item?.card_type,
    item?.cardType,
    item?.reward_rarity,
    item?.rewardRarity,
    item?.metadata?.rarity,
    item?.meta?.rarity,
    item?.config?.rarity,
    item?.card?.rarity,
  ];

  for (const possibleValue of possibleValues) {
    const rarity = String(
      possibleValue || ''
    )
      .trim()
      .toLowerCase();

    if (rarity.includes('legendary')) {
      return 'legendary';
    }

    if (rarity.includes('epic')) {
      return 'epic';
    }

    if (rarity.includes('rare')) {
      return 'rare';
    }

    if (rarity.includes('common')) {
      return 'common';
    }
  }

  const searchable =
    JSON.stringify(item || {})
      .toLowerCase();

  if (searchable.includes('legendary')) {
    return 'legendary';
  }

  if (searchable.includes('epic')) {
    return 'epic';
  }

  if (searchable.includes('rare')) {
    return 'rare';
  }

  return 'common';
};

const getCardRarityIcon = (item) => {
  switch (getPrizeCardRarity(item)) {
    case 'legendary':
      return '/legendary.png';

    case 'epic':
      return '/epic.png';

    case 'rare':
      return '/rare.png';

    default:
      return '/common.png';
  }
};

const normalizeChestKey = (value) => {
  const text = String(value || '')
    .trim()
    .toLowerCase();

  if (text.includes('diamond')) {
    return 'diamond';
  }

  if (
    text.includes('gold') ||
    text.includes('golden')
  ) {
    return 'golden';
  }

  if (text.includes('silver')) {
    return 'silver';
  }

  if (text.includes('wood')) {
    return 'wood';
  }

  if (text.includes('legendary')) {
    return 'legendary';
  }

  if (text.includes('epic')) {
    return 'epic';
  }

  if (text.includes('rare')) {
    return 'rare';
  }

  if (text.includes('common')) {
    return 'common';
  }

  return '';
};

const getPrizeChestIcon = (item) => {
  const chestType =
    item?.chest_type ??
    item?.chestType ??
    item?.display_name ??
    item?.name ??
    '';

  const key =
    normalizeChestKey(chestType);

  switch (key) {
    case 'wood':
      return '/shop_chest_5.png';

    case 'silver':
      return '/shop_chest_3.png';

    case 'golden':
      return '/shop_chest_1.png';

    case 'diamond':
      return '/shop_chest_2.png';

    case 'legendary':
      return '/legendary.png';

    case 'epic':
      return '/epic.png';

    case 'rare':
      return '/rare.png';

    case 'common':
      return '/common.png';

    default:
      return (
        item?.image ||
        item?.image_url ||
        item?.icon ||
        item?.icon_url ||
        item?.item_icon ||
        '/pack.png'
      );
  }
};

const getNamePrizeIcon = (item) => {
  const category = String(
    item?.name_category ??
      item?.nameCategory ??
      ''
  )
    .trim()
    .toLowerCase();

  const value = String(
    item?.name_value ??
      item?.nameValue ??
      item?.image ??
      item?.icon ??
      ''
  ).trim();

  if (value) {
    if (
      value.startsWith('/') ||
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('data:')
    ) {
      return value;
    }

    return `/name-cosmetics/${value}`;
  }

  if (
    category === 'banner' ||
    category === 'banners'
  ) {
    return '/banner_icon.png';
  }

  if (
    category === 'emoji' ||
    category === 'emogy' ||
    category === 'emojy'
  ) {
    return '/emogy_icon.png';
  }

  return '/style_icon.png';
};

function BattlePassPage({
  user,
  token,
  setUser,
  onBattlePassStatusChange,
  onOpenShop,
  onNavigate,
}) {
  const [data, setData] = useState({
    enabled: false,
    hasBattlePass: false,
    priceRunes: 0,
    priceStars: 0,
    multiplier: 1.7,
    wins: 0,
    seasonNumber: 1,
    endsAt: null,
    progression: [],
  });

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [purchasing, setPurchasing] =
    useState(false);

  const [claimingWins, setClaimingWins] =
    useState(null);

  const [error, setError] =
    useState('');

const [claimedReward, setClaimedReward] =
  useState(null);

const [showClaimModal, setShowClaimModal] =
  useState(false);

  const [showPurchaseModal, setShowPurchaseModal] =
    useState(false);

  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

    const [successMessage, setSuccessMessage] = useState('');

  const [showNoRunesModal, setShowNoRunesModal] =
    useState(false);

  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    finished: false,
  });

  const authHeaders = useMemo(() => {
    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  }, [token]);

  const normalizeResponse = useCallback(
    (raw) => {
      const source =
        raw?.value ??
        raw?.battlePass ??
        raw ??
        {};

      const progression = Array.isArray(
        source.progression
      )
        ? source.progression
            .map((row) => {
              const item =
                row?.item ??
                row?.prize ??
                null;

              const winsRequired =
                normalizeNumber(
                  row?.winsRequired ??
                    row?.wins_required,
                  0
                );

              return {
                winsRequired,
                prizeItemId:
                  row?.prizeItemId ??
                  row?.prize_item_id ??
                  item?.id ??
                  null,

                item,

                unlocked:
                  row?.unlocked !== undefined
                    ? normalizeBoolean(row.unlocked)
                    : normalizeNumber(
                        source.wins ??
                          source.currentWins,
                        0
                      ) >= winsRequired,

                claimed:
                  normalizeBoolean(
                    row?.claimed
                  ),
              };
            })
            .sort(
              (a, b) =>
                a.winsRequired -
                b.winsRequired
            )
        : [];

      return {
        enabled:
          source.enabled !== false,

        hasBattlePass:
          normalizeBoolean(
            source.hasBattlePass ??
              source.has_battle_pass ??
              source.active ??
              source.battlePassActive
          ),

        priceRunes:
          normalizeNumber(
            source.priceRunes ??
              source.price_runes,
            0
          ),

        priceStars:
          normalizeNumber(
            source.priceStars ??
              source.price_stars,
            0
          ),

        multiplier:
          normalizeNumber(
            source.multiplier,
            1.7
          ),

        wins:
          normalizeNumber(
            source.wins ??
              source.currentWins ??
              source.current_wins,
            0
          ),

        seasonNumber:
          normalizeNumber(
            source.seasonNumber ??
              source.season_number,
            1
          ),

        endsAt:
          source.endsAt ??
          source.ends_at ??
          null,

        progression,
      };
    },
    []
  );

  const loadBattlePass = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError('');

        const response = await fetch(
          `${API_URL}/api/battle-pass`,
          {
            headers: authHeaders,
          }
        );

        const responseData =
          await response
            .json()
            .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            responseData?.error ||
              'Failed to load Battle Pass'
          );
        }

        const normalized =
          normalizeResponse(responseData);

        setData(normalized);

        if (
  typeof onBattlePassStatusChange === 'function'
) {
  onBattlePassStatusChange(
    normalized.hasBattlePass
  );
}

        if (
          typeof setUser === 'function' &&
          normalized.hasBattlePass
        ) {
          setUser((previous) => ({
            ...previous,
            battlepass_active: true,
            battlePassActive: true,
          }));
        }
      } catch (requestError) {
        console.error(
          'Failed to load Battle Pass:',
          requestError
        );

        setError(
          requestError?.message ||
            'Failed to load Battle Pass'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      authHeaders,
      normalizeResponse,
      setUser,
      onBattlePassStatusChange,
    ]
  );

  useEffect(() => {
    loadBattlePass();
  }, [loadBattlePass]);

  useEffect(() => {
    if (!data.endsAt) {
      setCountdown({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        finished: false,
      });

      return undefined;
    }

    const tick = () => {
      const endTime =
        new Date(data.endsAt).getTime();

      if (!Number.isFinite(endTime)) {
        return;
      }

      const difference = Math.max(
        0,
        endTime - Date.now()
      );

      setCountdown({
        days: Math.floor(
          difference /
            (1000 * 60 * 60 * 24)
        ),

        hours: Math.floor(
          (difference /
            (1000 * 60 * 60)) %
            24
        ),

        minutes: Math.floor(
          (difference /
            (1000 * 60)) %
            60
        ),

        seconds: Math.floor(
          (difference / 1000) %
            60
        ),

        finished: difference === 0,
      });
    };

    tick();

    const interval =
      window.setInterval(tick, 1000);

    return () =>
      window.clearInterval(interval);
  }, [data.endsAt]);

  const progression = data.progression;

  const totalMilestones =
    progression.length;

  const claimedMilestones =
    progression.filter(
      (row) => row.claimed
    ).length;

  const nextMilestone = useMemo(() => {
    return (
      progression.find(
        (row) =>
          !row.claimed &&
          row.winsRequired > data.wins
      ) ||
      progression.find(
        (row) =>
          row.unlocked &&
          !row.claimed
      ) ||
      null
    );
  }, [progression, data.wins]);

  const progressPercent =
    useMemo(() => {
      if (!progression.length) {
        return 0;
      }

      const finalWins =
        progression[
          progression.length - 1
        ]?.winsRequired || 0;

      if (finalWins <= 0) {
        return 0;
      }

      return Math.min(
        100,
        Math.max(
          0,
          (data.wins / finalWins) *
            100
        )
      );
    }, [progression, data.wins]);

  const nextWinsRemaining =
    nextMilestone
      ? Math.max(
          0,
          nextMilestone.winsRequired -
            data.wins
        )
      : 0;


      const previewSide = useMemo(() => {
  const value = String(
    user?.side ??
      user?.card_side ??
      user?.cardSide ??
      user?.team ??
      'good'
  )
    .trim()
    .toLowerCase();

  if (
    value === 'bad' ||
    value === 'moon'
  ) {
    return 'bad';
  }

  return 'good';
}, [user]);

const battlePassPreviewCard =
  useMemo(() => {
    const parts = [
      'background',
      'body',
      'boots',
      'eyes',
      'face',
      'head',
      'mouth',
      'neck',
      'pants',
      'shirt',
      'weapons',
    ];

    const character = {};

    parts.forEach((part) => {
      character[part] = {
        side: previewSide,
        rarity: 'Common',
        variant: 1,
      };
    });

    return {
      level: 1,
      rarity: 'Common',
      attack: 3,
      defense: 2,
      health: 18,
      character,
    };
  }, [previewSide]);

const renderSeasonalSkinThumb = (
  item,
  size = 54
) => {
  const seasonName =
    item?.season_name ??
    item?.seasonName ??
    item?.season ??
    '';

  return (
    <div
     style={{
  position: 'relative',
  width: size,
  height: size,
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: 3,
  background: 'rgba(0, 0, 0, 0.25)',
  boxShadow:
    'inset 0 0 0 1px rgba(255,255,255,0.18), 0 2px 2px rgba(0,0,0,0.45)',
}}
    >
      {Object.entries(
        battlePassPreviewCard.character || {}
      )
        .sort(([partA], [partB]) => {
          const order = [
            'background',
            'body',
            'pants',
            'shirt',
            'boots',
            'neck',
            'head',
            'face',
            'eyes',
            'mouth',
            'weapons',
          ];

          const indexA =
            order.includes(partA)
              ? order.indexOf(partA)
              : order.length;

          const indexB =
            order.includes(partB)
              ? order.indexOf(partB)
              : order.length;

          return indexA - indexB;
        })
        .map(([part, partData]) => {
          

          const defaultSrc =
            `/assets/${part}/` +
            `${partData.side}/` +
            `${partData.rarity}/` +
            `${partData.variant}_thumb.png`;

          const src = getSkinSrc(
            seasonName,
            part,
            defaultSrc
          );

          return (
            <img
              key={part}
              src={src}
              alt=""
              draggable={false}
             onError={(event) => {
  const target = event.currentTarget;

  if (
    target.dataset.fallbackApplied !== 'true'
  ) {
    target.dataset.fallbackApplied = 'true';
    target.src = defaultSrc;
    return;
  }

  target.style.display = 'none';
}}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                imageRendering: 'pixelated',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          );
        })}
    </div>
  );
};

const getPrizeIcon = (item) => {
  const rawType =
    item?.type ??
    item?.item_type ??
    item?.itemType ??
    item?.kind ??
    '';

  const type =
    normalizePrizeType(rawType);

  const name = String(
    item?.name ??
      item?.display_name ??
      item?.displayName ??
      item?.label ??
      ''
  ).toLowerCase();

  if (
    type === 'card_pieces' ||
    name.includes('card pieces') ||
    name.includes('card fragment')
  ) {
    return '/pieces.png';
  }

  if (type === 'card') {
    return getCardRarityIcon(item);
  }

  switch (type) {
    case 'runes':
      return '/runes.png';

    case 'sparks':
      return '/sparks.png';

    case 'tapi':
      return '/icon_tapi.png';

    case 'booster':
      return '/energy.png';

    case 'ability':
      return '/fightlog.png';

    case 'shield':
      return '/shield.png';

    case 'card_pieces':
      return '/pieces.png';

    case 'spins':
      return '/fortuneicon.png';

    case 'evolve':
      return '/evolveicon.png';

    case 'gamepass':
      return '/passicon.png';

    case 'seasonal_accessory':
      return '/fragment.png';

    case 'name':
      return getNamePrizeIcon(item);

    case 'chest':
      return getPrizeChestIcon(item);

    default:
      return (
        item?.image ||
        item?.image_url ||
        item?.icon ||
        item?.icon_url ||
        item?.item_icon ||
        item?.itemIcon ||
        '/pack.png'
      );
  }
};

const renderPrizeVisual = (
  item,
  name
) => {
  const type = normalizePrizeType(
    item?.type ??
      item?.item_type ??
      item?.itemType ??
      item?.kind ??
      ''
  );

  const fragmentCount =
    getSeasonalFragmentCount(item);

  if (
    type === 'seasonal_accessory' &&
    fragmentCount === 1
  ) {
    return renderSeasonalSkinThumb(
      item,
      54
    );
  }

  return (
    <img
      src={getPrizeIcon(item)}
      alt={name}
      draggable={false}
      onError={(event) => {
        event.currentTarget.onerror =
          null;

        event.currentTarget.src =
          '/pack.png';
      }}
      style={{
        width: 32,
        height: 32,
        objectFit: 'contain',
        display: 'block',
      }}
    />
  );
};
  const pollStarsOrder = async (
    orderId,
    maxAttempts = 40,
    delayMs = 1500
  ) => {
    for (
      let attempt = 0;
      attempt < maxAttempts;
      attempt += 1
    ) {
      const response = await fetch(
        `${API_URL}/api/telegram/stars/order/${orderId}`,
        {
          headers: authHeaders,
        }
      );

      const responseData =
        await response
          .json()
          .catch(() => ({}));

      if (
        response.ok &&
        responseData?.status === 'paid'
      ) {
        return true;
      }

      if (
        response.ok &&
        responseData?.status === 'failed'
      ) {
        return false;
      }

      await wait(delayMs);
    }

    return false;
  };

  const completePurchase = async () => {
  setShowPurchaseModal(false);

  // Atualiza imediatamente todas as telas:
  // ProfileModal, Arenas e GamesTopBar.
  if (
    typeof onBattlePassStatusChange === 'function'
  ) {
    onBattlePassStatusChange(true);
  }

  // Atualiza imediatamente a própria página.
  setData((previous) => ({
    ...previous,
    hasBattlePass: true,
  }));

  // Mantém também o objeto global do usuário atualizado.
  if (
    typeof setUser === 'function'
  ) {
    setUser((previous) => ({
      ...previous,
      battlepass_active: true,
      battlePassActive: true,
    }));
  }

  // Confirma os dados atualizados no backend.
  await loadBattlePass({
    silent: true,
  });

  setShowSuccessModal(true);
};

  const purchaseWithRunes =
    async () => {
      if (purchasing) {
        return;
      }

      const currentRunes =
        normalizeNumber(
          user?.runes,
          0
        );

      if (
        currentRunes <
        data.priceRunes
      ) {
        setShowPurchaseModal(false);
        setShowNoRunesModal(true);
        return;
      }

      try {
        setPurchasing(true);
        setError('');

        const response = await fetch(
          `${API_URL}/api/battle-pass/purchase-runes`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              ...authHeaders,
            },
          }
        );

        const responseData =
          await response
            .json()
            .catch(() => ({}));

        if (!response.ok) {
          if (
            response.status === 403 &&
            responseData?.code ===
              'NEEDS_HUMAN_CHECK'
          ) {
            onNavigate?.(
              'humancheck',
              {
                returnTo:
                  'battlePass',
              }
            );

            return;
          }

          if (
            responseData?.code ===
              'NOT_ENOUGH_RUNES' ||
            responseData?.error ===
              'Not enough runes'
          ) {
            setShowPurchaseModal(false);
            setShowNoRunesModal(true);
            return;
          }

          if (
            responseData?.code ===
              'ALREADY_ACTIVE'
          ) {
            await completePurchase();
            return;
          }

          throw new Error(
            responseData?.error ||
              'Failed to purchase Battle Pass'
          );
        }

        if (
          typeof setUser ===
            'function'
        ) {
          setUser((previous) => ({
            ...previous,

            runes:
              responseData?.user
                ?.runes ??
              responseData
                ?.runes ??
              Math.max(
                0,
                normalizeNumber(
                  previous?.runes,
                  0
                ) -
                  data.priceRunes
              ),

            battlepass_active:
              true,

            battlePassActive:
              true,
          }));
        }

        await completePurchase();
      } catch (requestError) {
        console.error(
          'Rune purchase failed:',
          requestError
        );

        setError(
          requestError?.message ||
            'Failed to purchase Battle Pass'
        );
      } finally {
        setPurchasing(false);
      }
    };

  const purchaseWithStars =
    async () => {
      if (purchasing) {
        return;
      }

      try {
        setPurchasing(true);
        setError('');

        const response = await fetch(
          `${API_URL}/api/battle-pass/purchase-stars`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              ...authHeaders,
            },
          }
        );

        const responseData =
          await response
            .json()
            .catch(() => ({}));

        if (!response.ok) {
          if (
            response.status === 403 &&
            responseData?.code ===
              'NEEDS_HUMAN_CHECK'
          ) {
            onNavigate?.(
              'humancheck',
              {
                returnTo:
                  'battlePass',
              }
            );

            return;
          }

          if (
            responseData?.code ===
              'ALREADY_ACTIVE'
          ) {
            await completePurchase();
            return;
          }

          throw new Error(
            responseData?.error ||
              'Failed to create Telegram Stars payment'
          );
        }

        const invoiceLink =
          responseData?.invoiceLink ??
          responseData?.payment
            ?.invoiceLink ??
          null;

        const orderId =
          responseData?.orderId ??
          responseData?.payment
            ?.orderId ??
          null;

        if (
          !invoiceLink ||
          !orderId
        ) {
          throw new Error(
            'Invalid Telegram Stars payment response'
          );
        }

        const telegram =
          window.Telegram?.WebApp;

        if (
          !telegram?.openInvoice
        ) {
          throw new Error(
            'Telegram Stars payments are only available inside Telegram.'
          );
        }

        telegram.openInvoice(
          invoiceLink,
          async (status) => {
            if (
              status !== 'paid'
            ) {
              setPurchasing(false);
              return;
            }

            try {
              const confirmed =
                await pollStarsOrder(
                  orderId
                );

              if (!confirmed) {
                setError(
                  'Payment received, but confirmation is still pending.'
                );

                return;
              }

              await completePurchase();
            } catch (
              confirmationError
            ) {
              console.error(
                'Stars confirmation failed:',
                confirmationError
              );

              setError(
                'Payment was completed, but the Battle Pass status could not be refreshed.'
              );
            } finally {
              setPurchasing(false);
            }
          }
        );
      } catch (requestError) {
        console.error(
          'Stars purchase failed:',
          requestError
        );

        setError(
          requestError?.message ||
            'Failed to create Telegram Stars payment'
        );

        setPurchasing(false);
      }
    };

  const claimReward = async (
  milestone
) => {
  if (
    claimingWins !== null ||
    !data.hasBattlePass ||
    !milestone.unlocked ||
    milestone.claimed
  ) {
    return;
  }

  try {
    setClaimingWins(
      milestone.winsRequired
    );

    setError('');

    const response = await fetch(
      `${API_URL}/api/battle-pass/claim`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          ...authHeaders,
        },

        body: JSON.stringify({
          winsRequired:
            milestone.winsRequired,
        }),
      }
    );

    const responseData =
      await response
        .json()
        .catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        responseData?.error ||
          'Failed to claim reward'
      );
    }

    /*
     * Mark reward as claimed immediately.
     */
    setData((previous) => ({
      ...previous,

      progression:
        previous.progression.map(
          (row) =>
            row.winsRequired ===
            milestone.winsRequired
              ? {
                  ...row,
                  claimed: true,
                }
              : row
        ),
    }));

    /*
     * Update balances immediately without
     * reloading the page.
     */
    if (
      typeof setUser === 'function' &&
      responseData?.user
    ) {
      setUser((previous) => ({
        ...previous,
        ...responseData.user,
      }));
    }

    const reward =
      responseData?.reward || {};

    const itemType = String(
      reward?.itemType ||
      milestone.item?.type ||
      ''
    )
      .trim()
      .toLowerCase();

    const amount =
      normalizeNumber(
        reward?.amount ??
          milestone.item?.amount ??
          milestone.item?.runes_amount ??
          milestone.item?.sparks_amount ??
          milestone.item?.tapi_amount,
        1
      );

    setClaimedReward({
      name:
        milestone.item?.name ||
        'Battle Pass Reward',

      item:
        milestone.item || null,

      itemType,

      amount,

      balanceAfter:
        reward?.balanceAfter ?? null,
    });

    setShowClaimModal(true);
  } catch (requestError) {
    console.error(
      'Reward claim failed:',
      requestError
    );

    setError(
      requestError?.message ||
        'Failed to claim reward'
    );
  } finally {
    setClaimingWins(null);
  }
};

  const openPurchase = () => {
    if (
      data.hasBattlePass ||
      !data.enabled
    ) {
      return;
    }

    if (
      data.priceRunes <= 0 &&
      data.priceStars <= 0
    ) {
      setError(
        'Battle Pass is not for sale right now.'
      );

      return;
    }

    setShowPurchaseModal(true);
  };

  const renderCountdown = () => {
    if (!data.endsAt) {
      return 'Season end date unavailable';
    }

    if (countdown.finished) {
      return 'Battle Pass ended';
    }

    return (
      `${countdown.days}D ` +
      `${String(
        countdown.hours
      ).padStart(2, '0')}H ` +
      `${String(
        countdown.minutes
      ).padStart(2, '0')}M ` +
      `${String(
        countdown.seconds
      ).padStart(2, '0')}S`
    );
  };

  if (loading) {
  return (
    <div
      className="gamepass-container"
      style={{
        minHeight: 430,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoadingSpinner
        text="Loading OG PASS PLUS"
        size={64}
      />
    </div>
  );
}

  return (
    <>
      <div className="shop-container" style={{overflowX: "hidden"}}>
        <div
          className="gamepass-header"
          style={{
            height: 100,
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.65), rgba(0,0,0,0.05)), url('/moonsun.png'), url('/gamepassbg.png')",

            backgroundSize:
              '100% 100%, 70px, 100% 100%',

            backgroundRepeat:
              'no-repeat',

            backgroundPosition:
              'center, left 10px center, center',
          }}
        >
          <div className="gp-season-title">
            <div className="gp-season-label" style={{marginTop: 8}}>
              TAPTOPIA SEASON{' '}
              {data.seasonNumber}
            </div>

            <div
              className="gp-season-name shadow2"
              style={{
                color: "#fff",
                marginTop: 8,
                fontSize: 22,
              }}
            >
              OG PASS PLUS
            </div>
          </div>

          <div className="gp-season-timer">
            <span>
              Season ends in
            </span>

            <strong>
              {renderCountdown()}
            </strong>
          </div>
        </div>

        {error && (
          <div className="gp-error">
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              margin: '10px auto',
              padding: '9px 12px',
              maxWidth: 420,
              borderRadius: 8,
              background:
                'rgba(37, 157, 67, 0.85)',
              color: '#fff',
              fontWeight: 600,
              textAlign: 'center',
              textShadow:
                '1px 1px #000',
            }}
          >
            {successMessage}
          </div>
        )}

        <div
  style={{
    position: 'relative',
    margin: '14px auto 12px',
    maxWidth: 520,
    padding: '16px 16px 14px',
    borderRadius: 18,
    overflow: 'hidden',
    background:
      'linear-gradient(145deg, rgba(108, 49, 16, 0.96), rgba(58, 22, 8, 0.96))',
    border: '2px solid rgba(255, 205, 82, 0.62)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -5px 0 rgba(0,0,0,0.22), 0 7px 14px rgba(0,0,0,0.28)',
  }}
>
  {/* brilho decorativo */}
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      top: -55,
      left: '50%',
      width: 260,
      height: 120,
      transform: 'translateX(-50%)',
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(255,218,100,0.28) 0%, rgba(255,170,40,0.08) 45%, transparent 72%)',
      pointerEvents: 'none',
    }}
  />

  {/* linha superior */}
  <div
    style={{
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      gap: 10,
    }}
  >
    {/* vitórias */}
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          borderRadius: 14,
          background:
            'linear-gradient(180deg, rgba(255,196,62,0.28), rgba(78,25,5,0.45))',
          border: '1px solid rgba(255,215,105,0.48)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 7px rgba(0,0,0,0.28)',
        }}
      >
        <img
          src="/v2/trophy.png"
          alt="Battle wins"
          style={{
            width: 34,
            height: 34,
            objectFit: 'contain',
          }}
        />
      </div>

      <div style={{ minWidth: 0, textAlign: 'left' }}>
        <div
        className="shadow1"
          style={{
            fontSize: 11,
            fontWeight: 600,
            opacity: 0.78,
            letterSpacing: 0.7,
            whiteSpace: 'nowrap',
            color: "#fff"
          }}
        >
          YOUR WINS
        </div>

        <div className="shadow2"
          style={{
            marginTop: 1,
            fontSize: 32,
            lineHeight: 1,
            fontWeight: 600,
            color: '#fff',
            textShadow: '2px 2px 0 rgba(0,0,0,0.7)',
          }}
        >
          {data.wins}
        </div>
      </div>
    </div>

    {/* divisor central */}
    <div
      style={{
        width: 1,
        height: 52,
        background:
          'linear-gradient(180deg, transparent, rgba(255,220,130,0.48), transparent)',
      }}
    />

    {/* multiplicador */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 9,
        minWidth: 0,
      }}
    >
      <div style={{ minWidth: 0, textAlign: 'right' }}>
        <div className="shadow1"
          style={{
            fontSize: 11,
            fontWeight: 600,
            opacity: 0.78,
            letterSpacing: 0.7,
            whiteSpace: 'nowrap',
            color: "#fff"
          }}
        >
          WIN BONUS
        </div>

        <div className="shadow2"
          style={{
            marginTop: 1,
            fontSize: 32,
            lineHeight: 1,
            fontWeight: 600,
            color: '#ffe36e',
            textShadow:
              '2px 2px 0 rgba(0,0,0,0.72), 0 0 10px rgba(255,196,54,0.25)',
          }}
        >
          {data.multiplier}x
        </div>
      </div>

      <div
        style={{
          width: 48,
          height: 48,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          borderRadius: 14,
          background:
            'linear-gradient(180deg, rgba(255,224,100,0.30), rgba(92,37,5,0.46))',
          border: '1px solid rgba(255,224,105,0.52)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.2), 0 3px 7px rgba(0,0,0,0.28)',
        }}
      >
        <span
          style={{
            fontSize: 64,
            lineHeight: "48px",
            color: '#ffe36e',
            textShadow: '2px 2px 0 rgba(0,0,0,0.72)',
          }}
        >
          ×
        </span>
      </div>
    </div>
  </div>

  {/* descrição inferior */}
  <div
  className="shadow1"
    style={{
      position: 'relative',
      zIndex: 1,
      marginTop: 13,
      padding: '9px 11px',
      borderRadius: 11,
      background: 'rgba(0,0,0,0.22)',
      border: '1px solid rgba(255,255,255,0.08)',
      textAlign: 'center',
      fontSize: 13,
      lineHeight: 1.3,
      color: 'rgba(255,255,255,0.9)',
      textShadow: '1px 1px 0 rgba(0,0,0,0.75)',
    }}
  >
    {data.hasBattlePass ? (
      <>
        Your OG Pass Plus bonus is active.<br/><strong>Win battles to receive{' '}
        <strong style={{ color: '#ffe36e' }}>
          {data.multiplier}× rewards
        </strong></strong>
        .
      </>
    ) : (
      <>
        Unlock the Battle Pass to activate the{' '}
        <strong style={{ color: '#ffe36e' }}>
          {data.multiplier}× winner bonus
        </strong>{' '}
        and begin counting wins.
      </>
    )}
  </div>
</div>

<motion.button
  type="button"
  className="game-button green-button"

  style={{
    width: 'calc(100% - 28px)',
    maxWidth: 520,

    margin:
      '0 auto 14px',

    display:
      'flex',

    alignItems:
      'center',

    justifyContent:
      'center',

    gap: 8,
  }}

  whileHover={{
    scale:
      data.hasBattlePass
        ? 1
        : 1.03,
  }}

  whileTap={{
    scale:
      data.hasBattlePass
        ? 1
        : 0.97,
  }}

  onClick={
    openPurchase
  }

  disabled={
    data.hasBattlePass ||
    purchasing ||
    !data.enabled ||
    (
      data.priceRunes <= 0 &&
      data.priceStars <= 0
    )
  }
>
  {data.hasBattlePass ? (
    <span>
      OG PASS PLUS ACTIVE
    </span>
  ) : (
    !data.enabled ||
    (
      data.priceRunes <= 0 &&
      data.priceStars <= 0
    )
  ) ? (
    <span>
      OG PASS PLUS UNAVAILABLE
    </span>
  ) : data.priceRunes > 0 ? (
    <>
      <span>
        UNLOCK FOR
      </span>

      <img
        src="/runes.png"
        alt="Runes"

        style={{
          width: 28,
          height: 28,
          objectFit: 'contain',
        }}
      />

      <strong
        style={{
          fontSize: 22,
          textShadow:
            '1px 1px #000',
        }}
      >
        {data.priceRunes}
      </strong>
    </>
  ) : (
    <>
      <span>
        UNLOCK FOR
      </span>

      <img
        src="/tstar.svg"
        alt="Telegram Stars"

        style={{
          width: 28,
          height: 28,
          objectFit: 'contain',
        }}
      />

      <strong
        style={{
          fontSize: 22,
          textShadow:
            '1px 1px #000',
        }}
      >
        {data.priceStars}
      </strong>
    </>
  )}
</motion.button>

        <div className="gp-progress-wrapper">
          <div className="gp-progress-labels shadow1" style={{color: "#fff",fontWeight: 600}}>
            <span>
              Og Pass Plus Progress
            </span>

            <span>
              {claimedMilestones}/
              {totalMilestones} rewards
            </span>
          </div>

          <div className="gp-progress-bar">
            <div
              className="gp-progress-fill"
              style={{
                width:
                  `${progressPercent}%`,
              }}
            />
          </div>

         <div
  className="shadow1"
  style={{
    fontWeight: 600,
    marginTop: 8,
    textAlign: 'center',
    fontSize: 13,
    color: "#fff",
  }}
>
  {!data.hasBattlePass ? (
    <>
      <div
        style={{
          opacity: 0.9,
          marginBottom: 10,
        }}
      >
        Unlock the OG Plass Plus to start counting your wins.
      </div>

     {(
  !data.enabled ||
  (
    data.priceRunes <= 0 &&
    data.priceStars <= 0
  )
) && (
  <div style={{ opacity: 0.7 }}>
    OG Pass Plus is currently unavailable.
  </div>
)}
    </>
  ) : nextMilestone ? (
    nextWinsRemaining > 0 ? (
      `${nextWinsRemaining} more ${
        nextWinsRemaining === 1 ? "win" : "wins"
      } until the next reward`
    ) : (
      "You have an unlocked reward!"
    )
  ) : (
    "All Og Pass Plus rewards completed"
  )}
</div>
        </div>

        <div style={{marginLeft: -10, marginRight: -10}}>

        <div className="modal_title">
          OG Pass Plus Rewards
        </div>

        <div className="modal_body modal_body_2 nomodal" style={{paddingTop: 15}}>

        <div
          style={{
            display: 'grid',
          }}
        >
          {progression.length === 0 && (
            <div className="info"
              style={{
                padding: 20,
                textAlign: 'center',
                opacity: 0.7,
              }}
            >
              No Og Pass Plus rewards configured.
            </div>
          )}

          {progression.map(
            (milestone, index) => {
              const item =
                milestone.item;

              const lockedByPass =
                !data.hasBattlePass;

              const lockedByWins =
                !milestone.unlocked;

              const locked =
                lockedByPass ||
                lockedByWins;

              const claimable =
                data.hasBattlePass &&
                milestone.unlocked &&
                !milestone.claimed;

              const isClaiming =
                claimingWins ===
                milestone.winsRequired;

              const amount =
                item?.amount ??
                item?.value ??
                '';

              const name =
                item?.name ??
                item?.label ??
                'Prize';

              return (
                <motion.div
  key={`${milestone.winsRequired}-${milestone.prizeItemId || index}`}
  initial={{
    opacity: 0,
    y: 10,
  }}
  animate={{
    opacity: 1,
    y: 0,
  }}
  transition={{
    duration: 0.16,
    delay: index * 0.02,
  }}
  className="info"
  style={{
    display: 'grid',
    gridTemplateColumns: '64px minmax(0, 1fr) auto',
    columnGap: 2,
    alignItems: 'center',
    padding: '8px',
  }}
>
                 <div
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 3,
    minWidth: 0,
  }}
>
  <img
    src="/v2/trophy.png"
    alt="Wins"
    style={{
      width: 26,
      height: 26,
      objectFit: 'contain',
      flexShrink: 0,
    }}
  />

  <div
    style={{
      textAlign: 'center',
      lineHeight: 1,
      minWidth: 32,
    }}
  >
    <div
      style={{
        fontSize: 18,
        fontWeight: 600,
      }}
    >
      {milestone.winsRequired}
    </div>

    <div
      style={{
        marginTop: 3,
        fontSize: 11,
        opacity: 0.75,
      }}
    >
      WINS
    </div>
  </div>
</div>
                  <div
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    width: '100%',
  }}
>
                    <div
  style={{
    position: 'relative',
    flexShrink: 0,
    width: 42,
    height: 42,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  {renderPrizeVisual(
    item,
    name
  )}

  {locked && (
    <img
      src="/v2/lock.png"
      alt="Locked"
      style={{
        position: 'absolute',
        zIndex: 10,
        width: 16,
        right: 0,
        bottom: 0,
      }}
    />
  )}
</div>

                    <div
  style={{
    minWidth: 0,
    flex: 1,
    textAlign: 'left',
  }}
>
                      <div
                        style={{
                          fontWeight: 600,
                          overflow:
                            'hidden',
                          textOverflow:
                            'ellipsis',
                          whiteSpace:
                            'nowrap',
                        }}
                      >
                        {amount
                          ? `${name}`
                          : name}
                      </div>
                    </div>
                  </div>

                  <div>
                    {milestone.claimed ? (
                      <div className="game-button-small green-button"
                        style={{
                          fontSize: 11,
                        
                        }}
                      >
                        CLAIMED
                      </div>
                    ) : claimable ? (
                      <button
                        type="button"
                        className="game-button-small yellow-button"
                        disabled={
                          isClaiming
                        }
                        onClick={() =>
                          claimReward(
                            milestone
                          )
                        }
                      >
                        {isClaiming
                          ? 'CLAIM'
                          : 'CLAIM'}
                      </button>
                    ) : !data.hasBattlePass ? (
  <button
    type="button"
    className="game-button-small yellow-button"
    onClick={openPurchase}
   disabled={
  purchasing ||
  !data.enabled ||
  (
    data.priceRunes <= 0 &&
    data.priceStars <= 0
  )
}
    style={{
      minWidth: 72,
      paddingLeft: 9,
      paddingRight: 9,
      opacity:
  !data.enabled ||
  (
    data.priceRunes <= 0 &&
    data.priceStars <= 0
  )
    ? 0.55
    : 1,
    }}
  >
    UNLOCK
  </button>
) : (
  <button
    type="button"
    className="game-button-small red-button"
    disabled
    style={{
      minWidth: 72,
      paddingLeft: 8,
      paddingRight: 8,
      opacity: 0.8,
      cursor: "not-allowed",
    }}
  >
    {Math.max(
      0,
      milestone.winsRequired - data.wins
    )}{' '}
    MORE
  </button>
)}
                  </div>
                </motion.div>
              );
            }
          )}
        </div></div></div>

        <motion.button
          type="button"
          className="game-button green-button"
          style={{
  width: 'calc(100% - 28px)',
  maxWidth: 520,
  boxSizing: 'border-box',

  margin: '5px auto 10px',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
}}
          whileHover={{
            scale:
              data.hasBattlePass
                ? 1
                : 1.04,
          }}
          whileTap={{
            scale:
              data.hasBattlePass
                ? 1
                : 0.94,
          }}
          onClick={openPurchase}
          disabled={
  data.hasBattlePass ||
  !data.enabled ||
  (
    data.priceRunes <= 0 &&
    data.priceStars <= 0
  ) ||
  purchasing
}
        >
        {data.hasBattlePass ? (
  <span>OG PASS PLUS ACTIVE</span>
) : (
  !data.enabled ||
  (
    data.priceRunes <= 0 &&
    data.priceStars <= 0
  )
) ? (
  <span>OG PASS PLUS UNAVAILABLE</span>
) : data.priceRunes > 0 ? (
  <>
    <span>UNLOCK FOR</span>

    <img
      src="/runes.png"
      alt="Runes"
      style={{
        width: 28,
        height: 28,
        objectFit: 'contain',
      }}
    />

    <strong
      style={{
        fontSize: 22,
        textShadow: '1px 1px #000',
      }}
    >
      {data.priceRunes}
    </strong>
  </>
) : (
  <>
    <span>UNLOCK FOR</span>

    <img
      src="/tstar.svg"
      alt="Telegram Stars"
      style={{
        width: 28,
        height: 28,
        objectFit: 'contain',
      }}
    />

    <strong
      style={{
        fontSize: 22,
        textShadow: '1px 1px #000',
      }}
    >
      {data.priceStars}
    </strong>
  </>
)}
        </motion.button>

        {data.hasBattlePass && (
          <div className="shadow1"
            style={{
              color: "#fff",
              fontWeight: 600,
              textAlign: 'center',
              fontSize: 14,
              opacity: 0.85,
              paddingBottom: 14,
            }}
          >
            Your{' '}
            <strong>
              {data.multiplier}×
            </strong>{' '}
            battle reward bonus is active.
          </div>
        )}

        {refreshing && (
          <div
            style={{
              textAlign: 'center',
              fontSize: 12,
              opacity: 0.65,
            }}
          >
            Refreshing…
          </div>
        )}
      </div>

{showClaimModal && claimedReward && (
  <div
    className="modal_box"
    onClick={() => {
      setShowClaimModal(false);
      setClaimedReward(null);
    }}
  >
    <div className="modal_title">
      Reward Claimed

      <button
        type="button"
        className="modal_close"
        onClick={() => {
          setShowClaimModal(false);
          setClaimedReward(null);
        }}
      />
    </div>

    <motion.div
      className="modal_body"
      onClick={(event) =>
        event.stopPropagation()
      }
      initial={{
        scale: 0.8,
        opacity: 0,
      }}
      animate={{
        scale: 1,
        opacity: 1,
      }}
      exit={{
        scale: 0.92,
        opacity: 0,
      }}
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="modal_inside">
        <div
          style={{
            width: 80,
            height: 80,
            margin: '0 auto 0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              transform: 'scale(1.7)',
            }}
          >
            {renderPrizeVisual(
              claimedReward.item,
              claimedReward.name
            )}
          </div>
        </div>

        <div
          className="shadow2"
          style={{
            fontWeight: 600,
            fontSize: 25,
            marginBottom: 10,
            textAlign: 'center',
          }}
        >
          Reward Claimed!
        </div>

        <div
          className="info"
          style={{
            textAlign: 'center',
          }}
        >
          You received{' '}

          <strong>
            {claimedReward.name}
          </strong>

          {claimedReward.balanceAfter !== null && (
            <>
              <br />
              New balance:{' '}

              <strong>
                {claimedReward.balanceAfter}
              </strong>
            </>
          )}
        </div>
      </div>

      <div className="modal_footer">
        <button
          type="button"
          className="game-button green-button"
          onClick={() => {
            setShowClaimModal(false);
            setClaimedReward(null);
          }}
        >
          AWESOME!
        </button>
      </div>
    </motion.div>
  </div>
)}
      
        {showPurchaseModal && (
          <div
            className="modal_box"
            onClick={() => {
              if (!purchasing) {
                setShowPurchaseModal(
                  false
                );
              }
            }}
          >
          <div className="modal_title">Og Pass Plus Purchase
          <button onClick={() => {
              if (!purchasing) {
                setShowPurchaseModal(
                  false
                );
              }
            }} className="modal_close"/>
          </div>
            <motion.div
              className="modal_body"
              onClick={(event) =>
                event.stopPropagation()
              }
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              style={{
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="modal_inside"
                style={{
                  position:
                    'relative',
                  zIndex: 1,
                }}
              >
                <img
                  src="/finger.png"
                  alt="Battle Pass"
                  style={{
                    height: 80,
                    objectFit:
                      'contain',
                  }}
                />

                <div className="shadow2" style={{fontWeight: 600, fontSize: 24, marginBottom: 10}}>
                  Unlock OG Pass Plus
                </div>

                <div className="info">
                  Earn exclusive rewards and
                  receive{' '}
                  <strong>
                    {data.multiplier}×
                  </strong>{' '}
                  battle rewards when you win.
                </div>

              </div>

           <div
  className="modal_footer"
  style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'nowrap',
    width: '100%',
  }}
>
  {data.priceRunes > 0 && (
    <button
      type="button"
      className="game-button green-button"
      disabled={purchasing}
      onClick={purchaseWithRunes}
      style={{
        margin: 0,
        width: 'auto',
        minWidth: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingLeft: 12,
        paddingRight: 12,
      }}
    >
      <img
        src="/runes.png"
        alt="Runes"
        style={{
          width: 30,
          height: 30,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />

      <span>
        {purchasing
          ? 'PROCESSING...'
          : 'UNLOCK FOR'}
      </span>

      {!purchasing && (
        <strong
          style={{
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          {data.priceRunes}
        </strong>
      )}
    </button>
  )}

  {data.priceStars > 0 && (
    <button
      type="button"
      className="game-button green-button"
      disabled={purchasing}
      onClick={purchaseWithStars}
      style={{
        margin: 0,
        width: 'auto',
        minWidth: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingLeft: 12,
        paddingRight: 12,
      }}
    >
      <img
        src="/tstar.svg"
        alt="Telegram Stars"
        style={{
          width: 30,
          height: 30,
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />

      <span>
        {purchasing
          ? 'PROCESSING...'
          : `UNLOCK FOR ${data.priceStars}`}
      </span>
    </button>
  )}

  <button
    type="button"
    className="game-button red-button"
    disabled={purchasing}
    onClick={() =>
      setShowPurchaseModal(false)
    }
    style={{
      margin: 0,
      width: 'auto',
      minWidth: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 12,
      paddingRight: 12,
    }}
  >
    CANCEL
  </button>
</div>
            </motion.div>
          </div>
        )}
      

        {showSuccessModal && (
          <div
            className="modal_box"
            onClick={() =>
              setShowSuccessModal(
                false
              )
            }
          >
          <div className="modal_title">OG Pass Plus Purchase</div>
            <motion.div
              className="modal_body"
              onClick={(event) =>
                event.stopPropagation()
              }
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              style={{
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div className="modal_inside"
                style={{
                  position:
                    'relative',
                  zIndex: 1,
                }}
              >
                <img
                  src="/moonsun.png"
                  alt="Battle Pass"
                  style={{
                    width: 140,
                    height: 140,
                    objectFit:
                      'contain',
                  }}
                />

                <div className="shadow2"
                  style={{
                    fontWeight: 600, fontSize: 24, marginBottom: 10
                  }}
                >
                  OG Pass Plus Unlocked!
                </div>

                <div className="info">
                  Your{' '}
                  <strong>
                    {data.multiplier}×
                  </strong>{' '}
                  winner reward bonus is now
                  active.
                </div>
</div>

                <div className="modal_footer">
                  <button className="game-button red-button"
                    type="button"
                    onClick={() =>
                      setShowSuccessModal(
                        false
                      )
                    }
                  >
                    AWESOME!
                  </button>
                </div>
              
            </motion.div>
          </div>
        )}

      
        {showNoRunesModal && (
          <div
            className="modal-backdrop"
            onClick={() =>
              setShowNoRunesModal(
                false
              )
            }
          >
            <motion.div
              className="modal fancy-success-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
              initial={{
                scale: 0.8,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.8,
                opacity: 0,
              }}
            >
              <img
                src="/runes.png"
                alt="Not enough Runes"
                style={{
                  height: 80,
                  marginBottom: 10,
                }}
              />

              <h2
                style={{
                  color: '#ffcc00',
                }}
              >
                Not enough Runes
              </h2>

              <p>
                You need{' '}
                <strong>
                  {data.priceRunes}
                </strong>{' '}
                Runes to unlock the Battle
                Pass.
              </p>

              <div className="modal-footer">
                <button
                  type="button"
                  className="buy-runes-btn"
                  onClick={() => {
                    setShowNoRunesModal(
                      false
                    );

                    onOpenShop?.(
                      'coin_2'
                    );
                  }}
                >
                  Buy Runes
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setShowNoRunesModal(
                      false
                    )
                  }
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
    </>
  );
}

export default BattlePassPage;